import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";

/**
 * Interface representing an operator in the autocomplete dropdown
 * Defines the structure of each operator option
 *
 * @property id - Unique identifier for the operator
 * @property firstName - Operator's first name
 * @property lastName - Operator's last name
 */
interface OperatorOption {
    id: string;
    firstName: string;
    lastName: string;
}

/**
 * Props interface for the OperatorAutocomplete component
 * Extends the base autocomplete functionality with operator-specific features
 *
 * @property textFieldProps - Additional props to pass to the TextField component
 * @property form - Whether the component is used within a form
 * @property name - Name attribute for form integration
 * @property required - Whether the field is required in forms
 * @property setLoadingOptions - Callback to handle loading state changes
 * @property value - Currently selected operator option
 * @property onChange - Callback fired when selection changes
 */
interface OperatorAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<OperatorOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;

    [key: string]: any;
}

/**
 * Interface for the operator search API request
 * Defines the structure of the search request payload
 *
 * @property paginationInfo - Pagination configuration
 * @property paginationInfo.pageSize - Number of results per page
 * @property paginationInfo.currentPage - Current page number
 * @property filters - Search filters
 * @property filters.email - Filter by email address
 * @property filters.phone - Filter by phone number
 * @property filters.name - Filter by operator name
 */
interface OperatorSearchRequest {
    paginationInfo: {
        pageSize: number;
        currentPage: number;
    };
    filters: {
        email?: string;
        phone?: string;
        name?: string;
    };
}

/**
 * Interface for the operator search API response
 * Defines the structure of the search response data
 *
 * @property data - Response data container
 * @property data.resultList - Array of operator results
 * @property resultFlag - Indicates if the search was successful
 */
interface OperatorSearchResponse {
    data?: {
        resultList?: Array<{
            id: string;
            firstName: string;
            lastName: string;
        }>;
    };
    resultFlag?: boolean;
}

/**
 * OperatorAutocomplete Component
 *
 * A searchable dropdown component for selecting operators with the following features:
 * - Smart search that detects email, phone, or name input
 * - Async option loading with debouncing
 * - Loading state management
 * - Form integration support
 * - Customizable styling and behavior
 *
 * Search behavior:
 * - If input contains '@', searches by email
 * - If input contains numbers, searches by phone
 * - Otherwise, searches by name
 *
 * @component
 */
const OperatorAutocomplete = <M extends boolean = false, >({
                                                               textFieldProps = {},
                                                               form = false,
                                                               name = undefined,
                                                               required = false,
                                                               setLoadingOptions: setLoadingOptionsProps,
                                                               ...props
                                                           }: OperatorAutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches operator options based on search keywords
     * Intelligently determines search type (email/phone/name) based on input
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string
     * @returns Array of matching operator options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, _keywords?: string) => {
        setLoadingOptions(true);
        let request: OperatorSearchRequest = {
            paginationInfo: {pageSize: 10, currentPage: 1},
            filters: {},
        };
        const keywords = _keywords?.trim() ?? "";
        // Determine search type based on input format
        if (keywords.indexOf('@') !== -1) {
            request.filters.email = keywords;
        } else if (/\d/.test(keywords)) {
            request.filters.phone = keywords;
        } else {
            request.filters.name = !keywords?.length ? " " : keywords;
        }
        try {
            // @ts-ignore - API type definition is not complete
            const response = await Api.searchOperatorsLocal(request, cancelToken) as OperatorSearchResponse;
            if (!isMounted())
                return [];
            return response?.data?.resultList?.map(e => ({
                id: e.id,
                firstName: e.firstName,
                lastName: e.lastName,
            })) ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted]);

    /**
     * Determines if two operator options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: OperatorOption, value: OperatorOption | null) =>
        option.id === value?.id, []);

    /**
     * Formats the display label for an operator option
     * Combines first and last name, filtering out empty values
     */
    const getOptionLabel = useCallback((option: OperatorOption) =>
        [option?.firstName ?? "", option.lastName ?? ""].filter(Boolean).join(' ') ?? "", []);

    /**
     * Gets the unique key for an operator option
     */
    const getOptionKey = useCallback((option: OperatorOption) => option?.id ?? "", []);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<OperatorOption, M>
            canSearchEmptyKeywords
            keywordsBased
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default OperatorAutocomplete; 